import { Boxes } from "lucide-react";
import ToolLogo from "@/components/ToolLogo";
import { featuredTools } from "@/data/integrations";

// Two rings of recognizable logos orbiting the central "your software" core.
const byName = (n: string) => featuredTools.find((t) => t.name === n)!;
const INNER_TOOLS = ["Slack", "HubSpot", "Shopify", "Stripe", "Notion", "Zapier"].map(byName);
const OUTER_TOOLS = [
  "Outlook",
  "Zendesk",
  "Salesforce",
  "PayPal",
  "Airtable",
  "DATEV",
  "Microsoft Teams",
  "Google Workspace",
  "Pipedrive",
  "Intercom",
].map(byName);

const INNER_RADIUS = "clamp(80px, 21vw, 132px)";
const OUTER_RADIUS = "clamp(122px, 33vw, 208px)";

// Beams that pulse inward toward the core (angle in deg, delay in s).
const BEAMS = [
  { angle: 18, delay: 0 },
  { angle: 72, delay: 1.4 },
  { angle: 128, delay: 0.6 },
  { angle: 196, delay: 2.1 },
  { angle: 248, delay: 0.9 },
  { angle: 312, delay: 1.8 },
  { angle: 340, delay: 2.6 },
];

type Tool = { name: string; domain: string };

const OrbitRing = ({
  tools,
  radius,
  duration,
  direction,
  tileSize,
}: {
  tools: Tool[];
  radius: string;
  duration: string;
  direction: "cw" | "ccw";
  tileSize: number;
}) => (
  <div
    className={`orbit-ring orbit-ring--${direction}`}
    style={{ ["--orbit-dur" as string]: duration }}
  >
    {tools.map((tool, i) => {
      const angle = (360 / tools.length) * i;
      return (
        <div
          key={tool.domain}
          className="orbit-node"
          style={{
            ["--angle" as string]: `${angle}deg`,
            ["--radius" as string]: radius,
          }}
        >
          <div className="orbit-node__counter">
            <div className="orbit-node__tile" style={{ ["--angle" as string]: `${angle}deg` }}>
              <div
                className="group/logo flex items-center justify-center rounded-full border border-border bg-card/80 shadow-[0_4px_20px_-6px_hsl(var(--background))] backdrop-blur-sm transition-colors duration-300 hover:border-accent/50"
                style={{ width: tileSize, height: tileSize }}
              >
                <ToolLogo name={tool.name} domain={tool.domain} size={tileSize} className="h-6 max-w-[60%]" />
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const IntegrationOrbit = ({ coreLabel }: { coreLabel: string }) => (
  <div className="orbit-stage" aria-hidden>
    {/* glow backdrop */}
    <div className="pointer-events-none absolute left-1/2 top-1/2 h-2/3 w-2/3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/10 blur-3xl" />

    {/* orbit paths */}
    <div className="orbit-path" style={{ width: `calc(${INNER_RADIUS} * 2)`, height: `calc(${INNER_RADIUS} * 2)` }} />
    <div
      className="orbit-path"
      style={{ width: `calc(${OUTER_RADIUS} * 2)`, height: `calc(${OUTER_RADIUS} * 2)`, opacity: 0.6 }}
    />

    {/* inflowing data beams */}
    {BEAMS.map((b) => (
      <div
        key={b.angle}
        className="orbit-beam"
        style={{
          ["--angle" as string]: `${b.angle}deg`,
          ["--beam-len" as string]: OUTER_RADIUS,
        }}
      >
        <span
          className="orbit-beam__pulse"
          style={{ ["--beam-dur" as string]: "4.5s", ["--beam-delay" as string]: `${b.delay}s` }}
        />
      </div>
    ))}

    {/* rings */}
    <OrbitRing tools={OUTER_TOOLS} radius={OUTER_RADIUS} duration="60s" direction="ccw" tileSize={48} />
    <OrbitRing tools={INNER_TOOLS} radius={INNER_RADIUS} duration="42s" direction="cw" tileSize={56} />

    {/* central core */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <div className="relative flex aspect-square w-[clamp(96px,24vw,136px)] flex-col items-center justify-center gap-1.5 rounded-full border border-accent/40 bg-card/90 text-center shadow-[0_0_40px_-8px_hsl(var(--accent)/0.6)] backdrop-blur-md">
        <div className="ai-glow-halo" />
        <Boxes className="relative text-accent" size={26} />
        <span className="relative px-2 text-[11px] font-semibold leading-tight text-foreground">{coreLabel}</span>
      </div>
    </div>
  </div>
);

export default IntegrationOrbit;
